import { HttpClient, HttpContext, HttpErrorResponse, type HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { catchError, map, type Observable, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { LEAPBOT_ERROR_COPY } from '../../config/leapbot.config';
import { SKIP_ACTIVITY_LOG } from '../../interceptors/skip-activity-log.token';
import type { LeapBotErrorKind, LeapBotSendResult } from '../../interfaces/leapbot.interface';

/** Failure of a LeapBot request, already classified; `message` is the Spanish user-facing copy. */
export class LeapBotRequestError extends Error {
  constructor(readonly kind: LeapBotErrorKind) {
    super(LEAPBOT_ERROR_COPY[kind]);
    this.name = 'LeapBotRequestError';
  }
}

/** Maps an HTTP status (0 = no response) to the kind of failure shown to the user. */
export function classifyLeapBotStatus(status: number): LeapBotErrorKind {
  if (status === 0) return 'unreachable';
  if (status === 401) return 'session_expired';
  if (status === 403) return 'forbidden';
  if (status === 413) return 'payload_too_large';
  if (status === 429) return 'rate_limited';
  return 'rejected';
}

/** Input of a message sent to LeapBot. */
export interface LeapBotSendParams {
  /** Conversation id, used as LeapBot's `threadId` (a UUID). */
  threadId: string;
  /** Auth0 `sub` of the signed-in user. */
  userId: string;
  message: string;
  files: readonly File[];
}

/**
 * Sends messages to LeapBot (`POST /web/chat`). Authentication is the user's Auth0 access token,
 * attached by the Auth0 HTTP interceptor; no key or secret is handled here. LeapBot answers 202
 * and the answer itself arrives over the socket (see `LeapBotSocketService`).
 */
@Injectable({ providedIn: 'root' })
export class LeapBotApiService {
  private readonly http = inject(HttpClient);

  /** Whether the build has a LeapBot URL; the chat is disabled otherwise. */
  readonly isConfigured: boolean = environment.leapbot !== null;

  /**
   * Sends one message, with optional files, as an internal user.
   *
   * @returns How many files LeapBot rejected.
   * @throws {LeapBotRequestError} Through the observable error channel, classified by status.
   */
  sendMessage(params: LeapBotSendParams): Observable<LeapBotSendResult> {
    const config = environment.leapbot;
    if (!config) return throwError(() => new LeapBotRequestError('unreachable'));

    const body = new FormData();
    body.append('userId', params.userId);
    body.append('threadId', params.threadId);
    body.append('userType', 'internal');
    if (params.message.length > 0) body.append('message', params.message);
    for (const file of params.files) body.append('files', file, file.name);

    return this.http
      .post(`${config.apiUrl.replace(/\/+$/, '')}/web/chat`, body, {
        observe: 'response',
        context: new HttpContext().set(SKIP_ACTIVITY_LOG, true),
      })
      .pipe(
        map((response) => ({ filesRejected: readFilesRejected(response) })),
        catchError((error: unknown) =>
          throwError(() =>
            error instanceof HttpErrorResponse
              ? new LeapBotRequestError(classifyLeapBotStatus(error.status))
              : new LeapBotRequestError('unreachable'),
          ),
        ),
      );
  }
}

function readFilesRejected(response: HttpResponse<unknown>): number {
  const body = response.body;
  if (typeof body !== 'object' || body === null) return 0;
  const payload = (body as { operationPayload?: { filesRejected?: unknown } }).operationPayload; // LeapBot 202 body: { operationPayload: { filesRejected } }
  return typeof payload?.filesRejected === 'number' ? payload.filesRejected : 0;
}
